import * as Chess from 'chess.js'
import { Chessboard, ChessboardProvider, SparePiece, defaultPieces } from 'react-chessboard';
import React from 'react';
import { StockfishAdapter } from './engine/stockfishAdapter';
import { selectEnginePolicy, getFullMoveCount, shouldForceDraw, sideHasOnlyKing } from './engine/enginePolicy';
import { normalizeEngineEval, mergeEvalInfo } from './engine/evalHandler';
import { calculateIncome, getOpponentBudget } from './shop/economy';
import { generateShop } from './shop/shopGenerator';
import { Shop } from './shop/Shop';
import { Bench } from './bench/Bench';
import { extractWhitePiecesFromFen, generateBlackSetup, buildBattleFen } from './battle';
import { computeMaterial, hasSidePieces } from './battle/material';
import { determineBattleResult, getBaseGoldGain, buildResultSummary } from './battle/roundFlow';
import { ResultOverlay } from './battle/ResultOverlay';
import { EvalBar } from './battle/EvalBar';
import { OpponentPreviewCard } from './battle/OpponentPreviewCard';
import { fenFromPlacements } from './logic/fen';
import { PIECE_COSTS, FINAL_ROUND_NUMBER } from './constants';
import { createAugmentEventApi } from './augments/events';
import { createAugmentStoreAdapter } from './augments/storeAdapter';
import { generateAugmentShopOffers } from './augments/shopGenerator';
import { AugmentIds, AUGMENTS, AUGMENT_SHOP_ALLOWLIST } from './augments/catalog';
import { AugmentDock, AUGMENT_COLOR_STYLES, getAugmentHex, getAugmentBackground, describeAugment } from './augments/AugmentDock';
import { RNG } from './utils/rng';
import { WHITE_KING_ONLY_FEN, generateAugmentSeed, createBaseState, addStartingKing } from './state/initialState';
import { computeUnitIdForSquare, computePendingRewards, getEmptyPendingRewards, hasPendingRewards } from './state/units';
import {
  getPieceStrokeColor,
  buildColoredPiecesRenderer as buildPiecesRenderer,
  getUiPieceRenderer as getUiRenderer,
  ensureBenchPieceVariant as ensureBenchVariant,
  computeUiPosition as computePosition
} from './pieces/rendering';
import { shakeInvalidDrop } from './ui/feedback';
import { hitTestAugmentDock, hitTestSellPanel } from './ui/hitTest';
import { coordsToSquare } from './ui/boardInteractions';
import { preloadSounds, playMoveSound, playSound } from './audio/sounds';
// Square-based tint rendering removed; piece coloring handled via custom piece types


class Game extends React.Component {

  constructor(props) {
    super(props);
    this.tickRate = 1000;
    this.fenStringInputId = 'fenStringInput';
    this.moneyPerRound = 3;
    this.startingFenBeforeBattle = null;
    this.pieceCosts = { ...PIECE_COSTS };
    this.engine = new StockfishAdapter('stockfish.asm.js');
    this._consecutiveIllegalEngineMoves = 0;
    this._evalSmoothedPercent = 0.5;
    this._evalLastUpdateMs = 0;
    this.state = this.initState();
    // Augments: initialize store adapter and event API
    this._augmentStore = this.createGameAugmentStoreAdapter();
    this.augmentApi = createAugmentEventApi(this._augmentStore);
    this._pieceRendererCache = {};
    this._lastWhiteMoverUnitId = null;
    this._deliverMateUnitId = null;
    this._augmentGoldPending = 0;
    this._pendingTooltipFrame = null;
    this._draggingPieceActive = false;
    this._sellAutoExpandEnabled = false;
  }

  handlePieceDragBegin(/* piece, sourceSquare */) {
    this._draggingPieceActive = true;
    this._sellAutoExpandEnabled = true;
  }

  handlePieceDragEnd() {
    this._draggingPieceActive = false;
    this._sellAutoExpandEnabled = false;
  }

  isPieceDragActive() {
    return !!this._draggingPieceActive || !!this._dragOrigin || !!this.state.currentSpareOrigin;
  }

  handleSellPanelPointerEnter() {
    this.maybeExpandSellPanelForDrag();
  }

  maybeExpandSellPanelForDrag(evt = null) {
    if (!this.state.sellPanelCollapsed) return;
    if (!this._sellAutoExpandEnabled || !this.isPieceDragActive()) return;
    const event = evt || window._gc_last_pointer_event;
    if (!event) return;
    const el = document.querySelector('[data-sell-panel]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;
    const withinPanel = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    if (withinPanel) {
      this.setState({ sellPanelCollapsed: false });
    }
  }

  // Left column with flex layout to avoid absolute positioning issues across rounds
  renderLeftNav() {
    const showAugments = (this.state.phase === 'prep' && (((this.state.roundNumber || 1) > 2) || !!this.props.allowAugRound1));
    return (
      <div style={{ width: 280, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', gap: 0 }}>
        {this.renderOpponentPreviewCard()}
        <div style={{ flex: 1 }} />
        {showAugments ? <div style={{ marginTop: 12 }}>{this.renderAugmentDock(true)}</div> : null}
      </div>
    );
  }

  renderRightNav() {
    const shouldShowEval = (this.state.phase === 'prep') && !!this.state.showEnemyPreview;
    const showSellPanel = (this.state.phase === 'prep') && ((this.state.roundNumber || 1) >= 3);
    return (
      <div style={{ width: 280, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', gap: 0 }}>
        {shouldShowEval ? <div style={{ marginBottom: 12 }}>{this.renderEvalBar()}</div> : null}
        <div style={{ flex: 1 }} />
        {showSellPanel ? <div style={{ marginTop: 12 }}>{this.renderSellPanel()}</div> : null}
      </div>
    );
  }

  getPieceStrokeColor() {
    return getPieceStrokeColor(this.props.theme);
  }

  // Build and cache colored piece renderers (default and augment variants)
  buildColoredPiecesRenderer(applyStrokeOverride = false) {
    this._pieceRendererCache = this._pieceRendererCache || {};
    const strokeColor = applyStrokeOverride ? this.getPieceStrokeColor() : null;
    return buildPiecesRenderer(this._pieceRendererCache, applyStrokeOverride, strokeColor);
  }

  getUiPieceRenderer(pieceType) {
    this._pieceRendererCache = this._pieceRendererCache || {};
    return getUiRenderer(this._pieceRendererCache, this.props.theme, pieceType);
  }

  ensureBenchPieceVariant(basePieceType, colorKey, benchId) {
    this._pieceRendererCache = this._pieceRendererCache || {};
    return ensureBenchVariant(this._pieceRendererCache, this.props.theme, basePieceType, colorKey, benchId);
  }

  // Derive the UI position (PositionDataType) from chess.js plus augment colors
  // Accept optional overrides to avoid stale reads during batched state updates
  // augmentsByUnitIdOverride allows computing with a pending state update in-flight
  computeUiPosition(gameOverride = null, prepUnitIdBySquareOverride = null, unitIdBySquareOverride = null, augmentsByUnitIdOverride = null) {
    const game = gameOverride || this.state?.game;
    const prepMap = prepUnitIdBySquareOverride || this.state?.prepUnitIdBySquare || {};
    const battleMap = unitIdBySquareOverride || this.state?.unitIdBySquare || {};
    const augMap = augmentsByUnitIdOverride || this.state?.augmentsByUnitId || {};
    const piecesMap = this.buildColoredPiecesRenderer(false);
    return computePosition(game, prepMap, battleMap, augMap, piecesMap);
  }

  renderIncomeIndicator() {
    try {
      const money = this.state.money || 0;
      const income = this.getNextIncome(money);
      const maxDots = 3;
      const filled = Math.min(maxDots, Math.floor(money / 5));
      const dots = Array.from({ length: filled }, () => '@').join(' ');
      const title = `Next income: +$${income}. Gain money for each $5 saved, up to $15`;
      return (
        <span title={title} style={{ fontSize: 12, color: this.props.theme === 'dark' ? '#ddd' : '#444', marginTop: 2 }}>{dots}</span>
      );
    } catch (_) {
      return null;
    }
  }

  renderMoneyPill() {
    return (
      <div style={{ width: 560, boxSizing: 'border-box', margin: '0 auto', padding: '0 8px 0', display: 'flex', justifyContent: 'center' }}>
        <div
          title={`Next income: +${this.getNextIncome(this.state.money)}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            border: '1px solid #555',
            borderBottom: 'none',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            padding: '4px 18px',
            background: this.props.theme === 'dark' ? '#2a2a2a' : '#f7f7f7',
            fontSize: 15,
            fontWeight: 700
          }}
        >
          <span>${this.state.money}</span>
        </div>
      </div>
    );
  }

  renderMoneyPillInverted() {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: -1, userSelect: 'none', cursor: 'default' }}>
        <div
          title={`Interest: $${this.getNextIncome(this.state.money)} (one per $5 saved)`}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            border: '1px solid #555',
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderTopWidth: 1,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            padding: '4px 18px',
            background: this.props.theme === 'dark' ? '#2a2a2a' : '#f7f7f7',
            fontSize: 15,
            fontWeight: 700
          }}
        >
          <span>${this.state.money}</span>
          {(() => {
            const filled = Math.min(3, Math.floor((this.state.money || 0) / 5));
            const dot = '•';
            if (filled <= 0) {
              // Placeholder to preserve pill height only (no width)
              return (
                <span style={{ display: 'block', height: 10, marginTop: 0, marginBottom: -2 }} />
              );
            }
            const dots = Array.from({ length: filled }, () => dot).join(' ');
            return (
              <span style={{ fontSize: 12, lineHeight: '10px', marginTop: 0, marginBottom: -2 }}>{dots}</span>
            );
          })()}
        </div>
      </div>
    );
  }

  renderIncomeDots() {
    try {
      const money = this.state.money || 0;
      const filled = Math.min(3, Math.floor(money / 5));
      if (filled <= 0) return null;
      const dots = Array.from({ length: filled }, () => '@').join(' ');
      const title = 'Gain money for each $5 saved, up to $15';
      return (
        <span title={title} style={{ fontSize: 12, color: this.props.theme === 'dark' ? '#ddd' : '#444', marginTop: 2 }}>{dots}</span>
      );
    } catch (_) {
      return null;
    }
  }

  onBattleEnd() {
    const game = this.state.game;
    const { winner, reason, resultType } = determineBattleResult(game);
    let deliveredByUnitId = null;
    // If White delivered mate, attribute to last white mover when possible
    if (winner === 'White' && reason === 'Checkmate') {
      deliveredByUnitId = this._lastWhiteMoverUnitId || null;
    }
    // Use 1 second delay for checkmate (win/loss), show draws immediately
    const delayMs = reason === 'Checkmate' ? 1000 : 0;
    this.presentRoundResult({ winner, reason, resultType, deliveredByUnitId, delayMs });
  }

  presentRoundResult({ winner, reason, resultType, deliveredByUnitId = null, delayMs = 0 }) {
    // For round wins with delay, play sound earlier (500ms) before showing overlay (1000ms)
    const roundWinSoundScheduled = resultType === 'win' && delayMs > 0;
    if (roundWinSoundScheduled) {
      window.setTimeout(() => playSound('roundWin', 0.6), 500);
    }

    const finalize = () => {
      const currentRound = this.state.roundNumber || 1;
      const baseGain = getBaseGoldGain(resultType);
      const incomeGain = this.getNextIncome(this.state.money);
      
      // Emit augment events
      try {
        this.augmentApi.emitGameEnd(resultType, deliveredByUnitId || null);
        const board = this.state.game.board();
        const files = 'abcdefgh';
        for (let r = 0; r < 8; r++) {
          for (let c = 0; c < 8; c++) {
            const p = board[r][c];
            if (p && p.color === 'w') {
              const sq = files[c] + (8 - r);
              const uid = (this.state.unitIdBySquare || {})[sq] || null;
              if (uid) this.augmentApi.emitSurvive(uid);
            }
          }
        }
      } catch (_) {}
      
      const rewardDelta = this._augmentGoldPending || 0;
      const resultSummary = buildResultSummary({
        winner,
        reason,
        resultType,
        currentRound,
        baseGain,
        incomeGain,
        rewardDelta
      });
      
      // Play result sounds
      if (resultSummary.campaignComplete) {
        playSound('gameWin');
      } else if (resultSummary.playerLost) {
        playSound('gameLose');
      } else if (resultType === 'win' && !roundWinSoundScheduled) {
        // Play immediately if not already scheduled (e.g., skip button)
        playSound('roundWin', 0.6);
      } else if (resultType === 'draw') {
        playSound('gameDraw');
      }
      
      // Stop the engine to prevent further bestmove messages
      try { this.engine?.stop?.(); } catch (_) {}
      this.setState({
        showResultOverlay: true,
        resultSummary,
        overlayFade: false
      }, () => {
        window.requestAnimationFrame(() => this.setState({ overlayFade: true }));
      });
    };
    if (delayMs > 0) window.setTimeout(finalize, delayMs);
    else finalize();
  }

  acknowledgeResults() {
    const summary = this.state.resultSummary || {};
    const result = summary.winner === 'White' ? 'win' : (summary.winner === 'Black' ? 'loss' : 'draw');
    this.setState({ showResultOverlay: false }, () => this.handleRoundEnd(result));
  }

  continueCampaignRun() {
    this.setState((prev) => {
      const summary = prev.resultSummary || {};
      if (!summary.campaignComplete) return null;
      return {
        resultSummary: {
          ...summary,
          campaignComplete: false,
          reason: summary.normalReason || summary.reason
        }
      };
    });
  }

  showTestResultOverlay() {
    const baseGain = 3; // win preview
    const incomeGain = this.getNextIncome(this.state.money);
    const rewardDelta = 0;
    const totalGain = baseGain + incomeGain + rewardDelta;
    this.setState({ showResultOverlay: true, resultSummary: { winner: 'White', reason: 'Checkmate (test)', baseGain, incomeGain, rewardDelta, totalGain, resultType: 'win' } });
  }

  

  initState() {
    const empty = new Chess();
    const augSeed = generateAugmentSeed();
    const startingFen = WHITE_KING_ONLY_FEN;
    empty.load(startingFen);
    
    const baseState = createBaseState(augSeed, startingFen);
    baseState.game = empty;
    
    const stateWithKing = addStartingKing(baseState);
    const uiPosition = this.computeUiPosition(
      empty,
      stateWithKing.prepUnitIdBySquare,
      stateWithKing.unitIdBySquare,
      stateWithKing.augmentsByUnitId
    );
    const shop = generateShop();
    const augShopOffers = this.getAugmentShopOffers(
      2,
      stateWithKing.money,
      stateWithKing.augRollNonce,
      stateWithKing
    );
    const nextState = {
      ...stateWithKing,
      uiPosition,
      shop,
      augShopOffers
    };
    try { this.engine.dispose(); } catch (e) {}
    this.engine.init();
    this.engine.onBestMove((mv) => this.handleEngineBestMove(mv));
    this.engine.onInfo((info) => this.handleEngineInfo(info));
    this.engine.onError((e) => {
      console.warn('[engine] error, attempting fallback', e);
      this.showToast('Engine error. Trying fallback...');
    });
    return nextState;
  }

  // --- Augment Dock: Offer generation and reroll ---
  getAugmentShopOffers(count = 2, moneyOverride = null, nonceOverride = null, stateOverride = null) {
    const stateRef = stateOverride || this.state || {};
    return generateAugmentShopOffers(count, {
      money: moneyOverride != null ? moneyOverride : (stateRef.money || 0),
      nonce: nonceOverride != null ? nonceOverride : (stateRef.augRollNonce || 0),
      roundNumber: stateRef.roundNumber || 1,
      augSeed: stateRef.augSeed || 0
    });
  }

  handleAugmentReroll() {
    const cost = (this._augmentStore?.kingHas && this._augmentStore.kingHas(AugmentIds.ROYAL_DECREE)) ? 1 : 2;
    if ((this.state.money || 0) < cost) return;
    const newMoney = (this.state.money || 0) - cost;
    const nextNonce = (this.state.augRollNonce || 0) + 1;
    const offers = this.getAugmentShopOffers(2, newMoney, nextNonce);
    this.setState({ money: newMoney, augShopOffers: offers, augRollNonce: nextNonce });
  }

  // --- Augments: Store Adapter ---
  createGameAugmentStoreAdapter() {
    return createAugmentStoreAdapter({
      getState: () => this.state,
      setState: (updater) => this.setState(updater),
      getGoldPending: () => this._augmentGoldPending || 0,
      setGoldPending: (value) => { this._augmentGoldPending = value; }
    });
  }

  // --- Augments: Identity helpers ---
  ensurePrepUnitIdForSquare(square, pieceType) {
    const { unitId, updates } = computeUnitIdForSquare(this.state, square, pieceType);
    if (updates) {
      this.setState(updates);
    }
    return unitId;
  }

  applyPendingBenchRewards() {
    const rewards = computePendingRewards(this.state.pendingBenchRewards);
    if (rewards.length === 0) return;
    rewards.forEach((pt) => this.addBenchPiece(pt));
    this.setState({ pendingBenchRewards: getEmptyPendingRewards() });
  }

  render() {
    return (
        <>
          {null}
          <style>{`
            @keyframes gc-shake { 
              0% { transform: translateX(0); }
              20% { transform: translateX(-4px); }
              40% { transform: translateX(4px); }
              60% { transform: translateX(-3px); }
              80% { transform: translateX(3px); }
              100% { transform: translateX(0); }
            }
            .gc-shake { animation: gc-shake 300ms ease-in-out; }
            .gc-toast { position: fixed; left: 50%; top: 24px; transform: translateX(-50%); background: #333; color: #fff; padding: 14px 28px; padding-right: 48px; border-radius: 10px; font-size: 18px; font-weight: 600; z-index: 9999; box-shadow: 0 4px 16px rgba(0,0,0,0.3); display: flex; align-items: center; gap: 12px; }
            .gc-toast-close { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #fff; font-size: 24px; font-weight: 400; cursor: pointer; opacity: 0.7; padding: 4px 10px; line-height: 1; }
            .gc-toast-close:hover { opacity: 1; }
            @keyframes gc-pulse-blue {
              0% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.6); }
              70% { box-shadow: 0 0 0 8px rgba(66, 133, 244, 0); }
              100% { box-shadow: 0 0 0 0 rgba(66, 133, 244, 0); }
            }
            .gc-glow-blue { animation: gc-pulse-blue 1.6s ease-out infinite; }
          `}</style>
          {null}
          {null}
          <ChessboardProvider options={{
            id: 'grotto-board',
            position: this.state.uiPosition,
            pieces: this.buildColoredPiecesRenderer(false),
            // Disable animations during setup so augments apply instantly; enable during rounds
            showAnimations: this.state.phase !== 'prep',
            animationDurationInMs: this.state.phase !== 'prep' ? 330 : 0,
            onPieceDrop: (args) => this.onPieceDrop(args),
            // Hide the ghost piece that remains in the original square while dragging
            draggingPieceGhostStyle: { opacity: 0 }
          }}>
            <div style={{ width: 1152, margin: '0 auto', marginTop: (this.state.phase === 'prep' ? 8 : 0), color: this.props.theme === 'dark' ? '#f1f1f1' : '#000', display: 'flex', gap: 16, alignItems: 'stretch' }}>
              {/* Left Nav column */}
              {this.renderLeftNav()}
              {/* Main column */}
              <div style={{ flex: '0 0 560px', position: 'relative' }}>
                <div style={{ maxWidth: 560 }}>
                  <div style={{ position: 'relative', width: 560, height: 560 }}>
                    <Chessboard
                      boardWidth={560}
                      onPieceDragBegin={(piece, sourceSquare) => this.handlePieceDragBegin(piece, sourceSquare)}
                      onPieceDragEnd={() => this.handlePieceDragEnd()}
                    />
                    {this.state.phase === 'prep' ? this.renderOpponentPreviewOverlay() : null}
                  </div>
                  {this.state.phase === 'prep' ? this.renderBenchSparePieces() : null}
                  {this.state.phase === 'prep' ? this.renderShopSparePieces() : null}
                </div>
                {/* Next Round button moved into current moves row */}
                {this.state.showResultOverlay ? this.renderResultOverlay() : null}
              </div>
              {/* Right Nav column */}
              {this.renderRightNav()}
            </div>
          </ChessboardProvider>
          {this.state.toastMessage ? (
            <div className="gc-toast">
              <span>{this.state.toastMessage}</span>
              <button 
                onClick={() => this.dismissToast()} 
                className="gc-toast-close"
                aria-label="Close"
              >×</button>
            </div>
          ) : null}
        </>
      );
  }

  renderResultOverlay() {
    return (
      <ResultOverlay
        summary={this.state.resultSummary}
        roundNumber={this.state.roundNumber}
        overlayFade={this.state.overlayFade}
        onNewGame={() => this.confirmNewGame()}
        onContinueCampaign={() => this.continueCampaignRun()}
        onAcknowledge={() => this.acknowledgeResults()}
      />
    );
  }

  

  renderEarlyNextRoundButton() { return null; }

  handleEngineBestMove(move) {
    // Ignore engine moves if not in battle phase or if overlay is shown
    if (this.state.phase !== 'battle' || this.state.showResultOverlay) {
      return;
    }
    // Inspect the piece on the from-square for debugging mismatches
    let fromPiece = null;
    try { fromPiece = this.state.game.get(move.from); } catch (_) { fromPiece = null; }
    const result = this.state.game.move({ from: move.from, to: move.to, promotion: move.promotion || undefined });
    // Play sound for moves after animation completes (matches animationDurationInMs)
    // White plays all sounds, opponent plays special sounds only (no regular move sound)
    if (result !== null) {
      const isWhiteMove = result.color === 'w';
      setTimeout(() => playMoveSound(result, isWhiteMove), 300);
    }
    if (result === null) {
      console.warn('[engine] illegal move received', move, 'fromSquarePiece:', fromPiece);
      // Count consecutive illegal moves; after a few, hard reset engine to resync
      this._consecutiveIllegalEngineMoves = (this._consecutiveIllegalEngineMoves || 0) + 1;
      if (this._consecutiveIllegalEngineMoves >= 3) {
        console.warn('[engine] too many illegal moves in a row, reinitializing engine');
        try { this.engine?.dispose?.(); } catch (_) {}
        this.engine = new StockfishAdapter('stockfish.asm.js');
        this.engine.init();
        this.engine.onBestMove((mv) => this.handleEngineBestMove(mv));
        this.engine.onInfo((info) => this.handleEngineInfo(info));
        this.engine.onError((e) => {
          console.warn('[engine] error, attempting fallback', e);
          this.showToast('Engine error. Trying fallback...');
        });
      }
    }
    // Augments: emit events from the executed move
    try {
      const from = move.from;
      const to = move.to;
      // Identify attacker unitId (white only attribution)
      const wasWhiteMove = (result?.color === 'w');
      let attackerUnitId = null;
      if (wasWhiteMove) {
        attackerUnitId = this.state.unitIdBySquare?.[from] || null;
        this._lastWhiteMoverUnitId = attackerUnitId;
      }
      // Update mapping after move
      const map = { ...(this.state.unitIdBySquare || {}) };
      if (attackerUnitId) {
        delete map[from];
        map[to] = attackerUnitId;
      }
      // Emit onMove
      if (wasWhiteMove && attackerUnitId) {
        this.augmentApi.emitMove(attackerUnitId, from, to);
      }
      // Emit onCapture
      if (result?.flags && result.flags.includes('c')) {
        // victim type from move.san or from captured field
        const victimType = (result?.captured || '').toLowerCase();
        let victimUnitId = null;
        // For captures, the victim square is 'to' (except en passant, but chess.js sets captured)
        victimUnitId = this.state.unitIdBySquare?.[to] || null;
        if (wasWhiteMove && attackerUnitId) {
          this.augmentApi.emitCapture(attackerUnitId, victimUnitId, victimType);
        }
      }
      // Emit onCheck (basic: detect '+' or '#' in SAN if available)
      try {
        const san = result?.san || '';
        if (wasWhiteMove && attackerUnitId && (san.includes('+') || san.includes('#'))) {
          this.augmentApi.emitCheck(attackerUnitId);
        }
      } catch (_) {}
      this.setState(
        {
          fen: this.state.game.fen(),
          unitIdBySquare: map,
          uiPosition: this.computeUiPosition(this.state.game, this.state.prepUnitIdBySquare, map)
        },
        () => {
          if (this.state.game.game_over()) {
            this.onBattleEnd();
          } else {
            this.engineNextStep();
          }
        }
      );
    } catch (_) {
      this.setState({ fen: this.state.game.fen(), uiPosition: this.computeUiPosition(this.state.game) }, () => {
        if (this.state.game.game_over()) {
          this.onBattleEnd();
        } else {
          this.engineNextStep();
        }
      });
    }
  }

  handleEngineInfo(info) {
    const sideToMove = this._engineSearchTurn || this.state.game?.turn?.() || 'w';
    const normalized = normalizeEngineEval(info, sideToMove);
    if (!normalized) return; // keep previous eval
    const evalInfo = mergeEvalInfo(this.state.evalInfo, normalized);
    this.setState({ evalInfo });
  }

  addRightClickRemovalHandler() {
    const boardElement = document.getElementById('grotto-board');
    if (!boardElement) return;
    this._onContextMenu = (e) => this.onBoardContextMenu(e);
    boardElement.addEventListener('contextmenu', this._onContextMenu);
  }

  removeRightClickRemovalHandler() {
    const boardElement = document.getElementById('grotto-board');
    if (!boardElement || !this._onContextMenu) return;
    boardElement.removeEventListener('contextmenu', this._onContextMenu);
    this._onContextMenu = null;
  }

  onBoardContextMenu(e) {
    e.preventDefault();
    if (this.state.phase !== 'prep') return;
    const boardElement = e.currentTarget || document.getElementById('grotto-board');
    if (!boardElement) return;
    const rect = boardElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    const square = coordsToSquare(x, y, rect.width, 'white');
    if (!square) return;
    const piece = this.state.game.get(square);
    if (!piece) return;
    if (piece.color !== 'w') return;
    if (piece.type === 'k') return;
    // Transfer identity to bench
    const pieceType = 'w' + piece.type.toUpperCase();
    const unitIdMap = { ...(this.state.prepUnitIdBySquare || {}) };
    const unitId = unitIdMap[square] || null;
    this.state.game.remove(square);
    if (unitId) {
      delete unitIdMap[square];
      this.setState({ prepUnitIdBySquare: unitIdMap });
    }
    this.addBenchPiece(pieceType, unitId);
  }

  setCurrentSpare(origin, type, benchId = null) {
    console.log('[setCurrentSpare]', { origin, type, benchCount: this.state.bench[type] });
    // Avoid setState here to prevent re-render before drag starts
    this._dragOrigin = { origin, type, benchId };
    this._draggingPieceActive = true;
    this._sellAutoExpandEnabled = true;
  }

  rerollShop() {
    if (this.state.money < 1) return;
    const newMoney = this.state.money - 1;
    this.setState({ shop: generateShop(), money: newMoney });
  }

  purchaseFromShop(pieceType, index) {
    const typeKey = pieceType[1]?.toLowerCase();
    const cost = this.pieceCosts[typeKey] || 0;
    if (this.state.money < cost) return;
    const newMoney = this.state.money - cost;
    const newShop = [...this.state.shop];
    newShop.splice(index, 1);
    playSound('purchase');
    this.setState({ money: newMoney, shop: newShop, showShopHint: false, shopGlowActive: false, benchGlowActive: this.state.benchGlowAllowed }, () => this.addBenchPiece(pieceType));
  }

  stagePieceForSell(basePieceType, sourceSquare, piece) {
    if (basePieceType === 'wK') {
      this.showToast('You cannot sell the King');
      this.triggerShakeAnimation(this._dragOrigin?.origin || null, this._dragOrigin?.benchId || null, sourceSquare);
      return false;
    }
    const origin = this._dragOrigin?.origin || null;
    const isBoardSquare = typeof sourceSquare === 'string' && /^[a-h][1-8]$/.test(sourceSquare);
    const fromBench = piece?.isSparePiece && origin === 'bench';
    const fromBoard = !piece?.isSparePiece && isBoardSquare;
    if (!fromBench && !fromBoard) {
      this.showToast('Only pieces from your bench or board can be sold');
      return false;
    }
    if (fromBench) {
      const benchId = this._dragOrigin?.benchId;
      if (benchId == null) {
        this.showToast('Unable to stage this bench piece for selling');
        return false;
      }
    }
    if (this.state.sellStagedPiece) {
      this.cancelSellStaging(true);
    }
    const staged = {
      pieceType: basePieceType,
      unitId: null,
      origin: fromBench ? 'bench' : 'board',
      lastSquare: fromBoard ? sourceSquare : null,
      benchId: fromBench ? this._dragOrigin?.benchId ?? null : null,
      refundValue: this.getPieceSellValue(basePieceType)
    };
    const updates = { sellStagedPiece: staged, sellPanelCollapsed: false };
    if (fromBench) {
      const benchId = staged.benchId;
      const unitId = benchId != null ? (this.state.unitIdByBenchId || {})[benchId] || null : null;
      staged.unitId = unitId;
      // Remove after the current frame to avoid unmounting the draggable mid-drag.
      this.scheduleBenchUpdate(() => this.removeBenchPiece(basePieceType, benchId));
    } else if (fromBoard) {
      const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
      const unitId = prepNext[sourceSquare] || null;
      staged.unitId = unitId;
      try { this.state.game.remove(sourceSquare); } catch (_) {}
      if (unitId) delete prepNext[sourceSquare];
      updates.prepUnitIdBySquare = prepNext;
      updates.fen = this.state.game.fen();
      updates.uiPosition = this.computeUiPosition(this.state.game, prepNext);
    }
    this._dragOrigin = null;
    this._draggingPieceActive = false;
    this._sellAutoExpandEnabled = false;
    this.setState(updates);
    return true;
  }

  /**
   * Handle swapping two pieces on the board during prep phase
   * @param {string} sourceSquare - Square of the piece being dragged
   * @param {string} targetSquare - Square of the piece being dropped onto
   * @param {string} sourcePieceType - Base piece type of the source piece (e.g., 'wP')
   * @returns {boolean} Whether the swap was successful
   */
  handlePieceSwap(sourceSquare, targetSquare, sourcePieceType) {
    const sourcePiece = this.state.game.get(sourceSquare);
    const targetPiece = this.state.game.get(targetSquare);
    
    if (!sourcePiece || !targetPiece) {
      console.warn('[handlePieceSwap] missing piece', { sourcePiece, targetPiece });
      return false;
    }
    
    // Validate rank restrictions for pawns
    const sourceRank = parseInt(sourceSquare[1], 10);
    const targetRank = parseInt(targetSquare[1], 10);
    
    // Check if swapping would put a pawn on rank 1 or 8
    if (sourcePiece.type === 'p' && (targetRank === 1 || targetRank === 8)) {
      this.showToast(`Pawns cannot be placed on rank ${targetRank}`);
      this.triggerShakeAnimation(null, null, targetSquare);
      return false;
    }
    if (targetPiece.type === 'p' && (sourceRank === 1 || sourceRank === 8)) {
      this.showToast(`Pawns cannot be placed on rank ${sourceRank}`);
      this.triggerShakeAnimation(null, null, sourceSquare);
      return false;
    }
    
    // Check if swapping would put pieces outside ranks 1-4 (player area)
    if (targetRank >= 5) {
      this.showToast('You can only place pieces on ranks 1-4');
      this.triggerShakeAnimation(null, null, targetSquare);
      return false;
    }
    if (sourceRank >= 5) {
      this.showToast('You can only place pieces on ranks 1-4');
      this.triggerShakeAnimation(null, null, sourceSquare);
      return false;
    }
    
    // Remove both pieces
    this.state.game.remove(sourceSquare);
    this.state.game.remove(targetSquare);
    
    // Place each piece on the other's square
    const placedSource = this.state.game.put({ color: sourcePiece.color, type: sourcePiece.type }, targetSquare);
    const placedTarget = this.state.game.put({ color: targetPiece.color, type: targetPiece.type }, sourceSquare);
    
    if (!placedSource || !placedTarget) {
      console.error('[handlePieceSwap] game.put failed, attempting to restore');
      // Try to restore original positions
      this.state.game.remove(sourceSquare);
      this.state.game.remove(targetSquare);
      this.state.game.put({ color: sourcePiece.color, type: sourcePiece.type }, sourceSquare);
      this.state.game.put({ color: targetPiece.color, type: targetPiece.type }, targetSquare);
      return false;
    }
    
    // Update unit ID mappings
    const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
    const sourceUnitId = prepNext[sourceSquare] || null;
    const targetUnitId = prepNext[targetSquare] || null;
    
    // Clear both squares first
    delete prepNext[sourceSquare];
    delete prepNext[targetSquare];
    
    // Assign unit IDs to new positions
    if (sourceUnitId) prepNext[targetSquare] = sourceUnitId;
    if (targetUnitId) prepNext[sourceSquare] = targetUnitId;
    
    // Update king unit ID if necessary
    let kingUnitIdNext = this.state.kingUnitId;
    if (sourcePieceType === 'wK' && sourceUnitId) {
      kingUnitIdNext = sourceUnitId;
    } else if (targetPiece.type === 'k' && targetUnitId) {
      kingUnitIdNext = targetUnitId;
    }
    
    const uiPos = this.computeUiPosition(this.state.game, prepNext, this.state.unitIdBySquare);
    const updates = {
      fen: this.state.game.fen(),
      prepUnitIdBySquare: prepNext,
      uiPosition: uiPos,
      currentSpareOrigin: null,
      currentSpareType: null
    };
    
    if (kingUnitIdNext !== this.state.kingUnitId) {
      updates.kingUnitId = kingUnitIdNext;
    }
    
    this._dragOrigin = null;
    // Play castle sound for piece swap immediately (no animation in setup)
    playSound('castle');
    this.setState(updates, () => {
      console.log('[handlePieceSwap] success');
      // Update engine position during prep to keep eval live
      if (this.state.phase === 'prep') {
        this.updateStockfishPosition();
        this.engine.goMovetime(200);
      }
    });
    
    return true;
  }

  onPieceDrop({ sourceSquare, targetSquare, piece }) {
    this._draggingPieceActive = false;
    this._sellAutoExpandEnabled = false;
    if (this.state.phase !== 'prep') {
      return false;
    }
    // Normalize piece type from either object or string (may include color variant suffix like 'wP_red')
    const rawPieceType = (typeof piece === 'string') ? piece : (piece?.pieceType || piece?.type || '');
    const pieceType = rawPieceType;
    const baseMatch = String(pieceType).match(/^([wb])([pnbrqk])/i);
    const basePieceType = baseMatch ? (baseMatch[1].toLowerCase() + baseMatch[2].toUpperCase()) : pieceType;
    // Block black pieces
    if (basePieceType[0] === 'b') {
      return false;
    }
    // Dropped off board (augment slot interception, benching, or ignore)
    if (!targetSquare) {
      // First, treat off-board drops over the Augment Dock as equip staging
      const overAugTile = hitTestAugmentDock();
      if (overAugTile != null) {
        const isBoardSquare = typeof sourceSquare === 'string' && /^[a-h][1-8]$/.test(sourceSquare);
        let unitId = null;
        if (piece?.isSparePiece) {
          const benchId = this._dragOrigin?.benchId ?? null;
          unitId = (this.state.unitIdByBenchId || {})[benchId] || null;
          if (!unitId) return false;
          // Consume the bench item (deferred to avoid mid-drag unmount)
          this.scheduleBenchUpdate(() => this.removeBenchPiece(basePieceType, benchId));
        } else if (isBoardSquare) {
          const currentMap = (this.state.prepUnitIdBySquare || {});
          const existingUnitId = currentMap[sourceSquare] || null;
          let nextUnitIdVal = this.state.nextUnitId || 1;
          if (existingUnitId) {
            unitId = existingUnitId;
          } else {
            unitId = 'u' + nextUnitIdVal;
            nextUnitIdVal = nextUnitIdVal + 1;
          }
          try { this.state.game.remove(sourceSquare); } catch (_) {}
          const newFen = this.state.game.fen();
          const prep = { ...currentMap };
          if (existingUnitId) delete prep[sourceSquare];
          this.setState({ fen: newFen, prepUnitIdBySquare: prep, uiPosition: this.computeUiPosition(this.state.game, prep), nextUnitId: nextUnitIdVal });
        }
        if (!unitId) return false;
        const staged = [...(this.state.augDockStaged || [null, null])];
        // If a piece is already staged in this slot, return it to the bench first
        if (staged[overAugTile]) {
          const prev = staged[overAugTile];
          try { this.addBenchPiece(prev.pieceType, prev.unitId); } catch (_) {}
        }
        // Remember the last board square when staging from the board to aid king restoration
        const lastSquare = isBoardSquare ? sourceSquare : null;
        staged[overAugTile] = { unitId, pieceType: basePieceType, lastSquare };
        this.setState({ augDockStaged: staged });
        this._dragOrigin = null;
        this._draggingPieceActive = false;
        this._sellAutoExpandEnabled = false;
        return true;
      }
      const overSellPanel = hitTestSellPanel();
      if (overSellPanel) {
        const staged = this.stagePieceForSell(basePieceType, sourceSquare, piece);
        return !!staged;
      }
      // Do not allow removing the only white king
      if (basePieceType === 'wK') {
        return false;
      }
      // If dropped off-board and came from a real board square, move it to bench
      const isBoardSquare = typeof sourceSquare === 'string' && /^[a-h][1-8]$/.test(sourceSquare);
      if (isBoardSquare && !piece?.isSparePiece) {
        const unitIdMap = { ...(this.state.prepUnitIdBySquare || {}) };
        const unitId = unitIdMap[sourceSquare] || null;
        this.state.game.remove(sourceSquare);
        if (unitId) delete unitIdMap[sourceSquare];
        // Update board position then update bench and identity
        const newFen = this.state.game.fen();
        this.setState({ fen: newFen, prepUnitIdBySquare: unitIdMap, uiPosition: this.computeUiPosition(this.state.game, unitIdMap) }, () => {
          this.addBenchPiece(basePieceType, unitId);
        });
        this._dragOrigin = null;
        return true;
      }
      // If it's a spare piece dragged off-board, ignore without changing bench
      return false;
    }
    // If dropped onto the same square, treat as a no-op
    if (sourceSquare && sourceSquare === targetSquare) {
      return true;
    }
    // Check if target square is occupied
    const targetOccupant = this.state.game.get(targetSquare);
    if (targetOccupant) {
      // Allow swapping two board pieces during prep phase
      const isBoardToBoard = !piece?.isSparePiece && typeof sourceSquare === 'string' && /^[a-h][1-8]$/.test(sourceSquare);
      const isWhiteOccupant = targetOccupant.color === 'w';
      
      if (isBoardToBoard && isWhiteOccupant) {
        return this.handlePieceSwap(sourceSquare, targetSquare, basePieceType);
      }
      
      return false;
    }
    
    // Disallow placing white pieces on ranks 5-8 during setup
    const rankNum = parseInt(targetSquare?.[1], 10);
    if (rankNum >= 5) {
      const origin = this._dragOrigin?.origin || this.state.currentSpareOrigin;
      this.triggerShakeAnimation(origin, this._dragOrigin?.benchId || null, targetSquare);
      this.showToast('You can only place pieces on ranks 1-4');
      return false;
    }
    // Extract the actual type letter from the base piece type (ignore variant suffix)
    const typeLower = basePieceType[1] ? basePieceType[1].toLowerCase() : '';
    // Disallow placing pawns on rank 1 or 8 (invalid in chess.js)
    // Check BEFORE removing piece from source to avoid corrupting game state
    if (typeLower === 'p' && (rankNum === 1 || rankNum === 8)) {
      const origin = this._dragOrigin?.origin || this.state.currentSpareOrigin;
      this.triggerShakeAnimation(origin, this._dragOrigin?.benchId || null, targetSquare);
      this.showToast(`Pawns cannot be placed on rank ${rankNum}`);
      return false;
    }
    // Prepare a local prep map to avoid intermediate setState races
    let prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
    let kingUnitIdNext = this.state.kingUnitId;
    // If not spare, remove from source
    if (!piece?.isSparePiece && sourceSquare) {
      this.state.game.remove(sourceSquare);
      // Move identity mapping along with the piece during prep
      if (prepNext[sourceSquare]) {
        const uid = prepNext[sourceSquare];
        delete prepNext[sourceSquare];
        prepNext[targetSquare] = uid;
        if (basePieceType === 'wK') kingUnitIdNext = uid;
      }
    }
    const success = this.state.game.put({ color: 'w', type: typeLower }, targetSquare);
    if (!success) {
      return false;
    }
    // If placed from bench, decrement bench using origin flag
    let origin = null;
    if (typeof piece?.position === 'string' && piece.position.startsWith('shop-')) origin = 'shop';
    if (!origin) origin = this._dragOrigin?.origin || this.state.currentSpareOrigin;
    if (piece?.isSparePiece && origin === 'bench') {
      const benchId = this._dragOrigin?.benchId ?? null;
      // Bind the unitId for this bench item to the target square identity
      if (benchId != null) {
        const uid = (this.state.unitIdByBenchId || {})[benchId] || null;
        if (uid) {
          prepNext[targetSquare] = uid;
          if (basePieceType === 'wK') kingUnitIdNext = uid;
        }
      }
      // Delay the bench mutation so dnd-kit finalizes the spare drag cleanly.
      this.scheduleBenchUpdate(() => this.removeBenchPiece(basePieceType, benchId));
      if (this.state.showBenchHint) {
        this.setState({ showBenchHint: false });
      }
      if (!this.state.showStartGlow && this.state.startGlowAllowed) {
        this.setState({ showStartGlow: true });
      }
      if (this.state.benchGlowAllowed) {
        this.setState({ benchGlowAllowed: false });
      }
    }
    this._dragOrigin = null;
    const uiPos = this.computeUiPosition(this.state.game, prepNext, this.state.unitIdBySquare);
    const endUpdates = { fen: this.state.game.fen(), currentSpareOrigin: null, currentSpareType: null, prepUnitIdBySquare: prepNext, uiPosition: uiPos };
    if (kingUnitIdNext !== this.state.kingUnitId) endUpdates.kingUnitId = kingUnitIdNext;
    // Play move sound for piece placement immediately (no animation in setup)
    playSound('moveSelf');
    this.setState(endUpdates, () => {
      // Update engine position during prep to keep eval live
      if (this.state.phase === 'prep') {
        this.updateStockfishPosition();
        // brief search for preview
        this.engine.goMovetime(200);
      }
    });
    return true;
  }

  componentDidMount() {
    window.setTimeout(() => this.tick(), 0);
    this.addRightClickRemovalHandler();
    this.resetAutoPreviewTimer();
    // Preload sound effects
    preloadSounds();
    // Track last pointer position globally for augment dock hit-testing and board bounds detection
    this._onPointerMove = (e) => {
      window._gc_last_pointer_event = e;
      if (this.isPieceDragActive()) {
        this.maybeExpandSellPanelForDrag(e);
      }
    };
    window.addEventListener('pointermove', this._onPointerMove, { passive: true });
    this._onPointerUp = () => {
      this._draggingPieceActive = false;
      this._sellAutoExpandEnabled = false;
    };
    window.addEventListener('pointerup', this._onPointerUp, { passive: true });
    // Kick the first short evaluation after mount to avoid setState during construction
    try {
      this.updateStockfishPosition();
      this.engine.goMovetime(200);
    } catch (_) {}
    this.updateBoardPieceTooltips();
  }

  componentWillUnmount() {
    this.removeRightClickRemovalHandler();
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    if (this._autoPreviewTimer) {
      clearTimeout(this._autoPreviewTimer);
      this._autoPreviewTimer = null;
    }
    if (this._onPointerMove) {
      window.removeEventListener('pointermove', this._onPointerMove);
      this._onPointerMove = null;
    }
    if (this._onPointerUp) {
      window.removeEventListener('pointerup', this._onPointerUp);
      this._onPointerUp = null;
    }
    if (this._pendingTooltipFrame != null) {
      try {
        window.cancelAnimationFrame(this._pendingTooltipFrame);
      } catch (_) {}
      this._pendingTooltipFrame = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.uiPosition !== this.state.uiPosition ||
      prevState.augmentsByUnitId !== this.state.augmentsByUnitId ||
      prevState.unitIdBySquare !== this.state.unitIdBySquare ||
      prevState.prepUnitIdBySquare !== this.state.prepUnitIdBySquare
    ) {
      this.updateBoardPieceTooltips();
    }
  }

  handleAugmentBuy(offerIndex) {
    try {
      const staged = (this.state.augDockStaged || [])[offerIndex] || null;
      if (!staged) return false;
      const pieceType = staged.pieceType;
      const unitId = staged.unitId;
      const augmentId = (this.state.augShopOffers || [])[offerIndex];
      if (!augmentId) return false;
      // King-only constraints
      const aug = AUGMENTS[augmentId];
      const isKingOnly = !!aug?.kingOnly;
      const isKingPiece = pieceType === 'wK';
      if (isKingOnly && !isKingPiece) {
        this.showToast('This augment can only be equipped on the King');
        return false;
      }
      const price = aug?.priceGold || 0;
      if ((this.state.money || 0) < price) {
        this.showToast('Not enough $');
        return false;
      }
      // One augment per piece; allow replace confirm
      const already = this.state.augmentsByUnitId?.[unitId]?.augmentId || null;
      if (already && already !== augmentId) {
        const ok = window.confirm('Replace existing augment? This permanently deletes the old one.');
        if (!ok) return false;
      }
      // Special handling: King cannot be benched. Ensure a board square is available and place it now.
      let kingPrepNext = null;
      let kingFenAfter = null;
      if (isKingPiece) {
        const preferred = staged.lastSquare || null;
        let targetSquare = null;
        try {
          if (preferred) {
            const occ = this.state.game.get(preferred);
            if (!occ) targetSquare = preferred;
          }
        } catch (_) {}
        if (!targetSquare) {
          const files = 'abcdefgh';
          outer_k: for (let rank = 1; rank <= 4; rank++) {
            for (let fi = 0; fi < files.length; fi++) {
              const sq = files[fi] + rank;
              try {
                const occ = this.state.game.get(sq);
                if (!occ) { targetSquare = sq; break outer_k; }
              } catch (_) {}
            }
          }
        }
        if (!targetSquare) {
          this.triggerShakeAnimation(null, null, null);
          this.showToast('No space to place the King. Clear a square first.');
          return false;
        }
        const placed = this.state.game.put({ color: 'w', type: 'k' }, targetSquare);
        if (!placed) {
          this.triggerShakeAnimation(null, null, targetSquare);
          this.showToast('Failed to place the King. Try a different square.');
          return false;
        }
        kingPrepNext = { ...(this.state.prepUnitIdBySquare || {}) };
        kingPrepNext[targetSquare] = unitId;
        kingFenAfter = this.state.game.fen();
      }
      // Deduct cost and bind augment
      const money = (this.state.money || 0) - price;
      const augments = { ...(this.state.augmentsByUnitId || {}) };
      augments[unitId] = { unitId, augmentId };
      // Update king reference if equipping on king
      const updates = { money, augmentsByUnitId: augments };
      if (isKingPiece) updates.kingUnitId = unitId;
      // Clear staged slot and return piece to bench (secondary bench behavior)
      const stagedSlots = [...(this.state.augDockStaged || [null, null])];
      stagedSlots[offerIndex] = null;
      // Consume the augment offer for this slot for the current round
      const offers = [...(this.state.augShopOffers || [])];
      offers[offerIndex] = null;
      if (isKingPiece) {
        const merged = { ...updates, augDockStaged: stagedSlots, augShopOffers: offers };
        if (kingPrepNext && kingFenAfter) {
          merged.prepUnitIdBySquare = kingPrepNext;
          merged.kingUnitId = unitId;
          merged.fen = kingFenAfter;
          // Compute UI in the same render so the tinted King appears immediately
          merged.uiPosition = this.computeUiPosition(this.state.game, kingPrepNext, this.state.unitIdBySquare, augments);
        }
        this.setState(merged);
      } else {
        // If piece came from the bench (no lastSquare), return it to the bench
        if (!staged.lastSquare) {
          this.setState({ ...updates, augDockStaged: stagedSlots, augShopOffers: offers }, () => this.addBenchPiece(pieceType, unitId));
        } else {
          // Piece came from board - try to place it back on its original square, otherwise find an empty square, otherwise bench
          const preferred = staged.lastSquare;
          const typeLower = pieceType[1] ? pieceType[1].toLowerCase() : null;
          let targetSquare = null;
          if (typeLower) {
            try {
              if (preferred) {
                const occ = this.state.game.get(preferred);
                if (!occ) targetSquare = preferred;
              }
            } catch (_) {}
            if (!targetSquare) {
              const files = 'abcdefgh';
              outer_piece: for (let rank = 1; rank <= 4; rank++) {
                for (let fi = 0; fi < files.length; fi++) {
                  const sq = files[fi] + rank;
                  try {
                    const occ = this.state.game.get(sq);
                    if (!occ) { targetSquare = sq; break outer_piece; }
                  } catch (_) {}
                }
              }
            }
          }
          if (targetSquare && typeLower) {
            const placed = this.state.game.put({ color: 'w', type: typeLower }, targetSquare);
            if (placed) {
              const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
              prepNext[targetSquare] = unitId;
              const fenAfter = this.state.game.fen();
              const merged = { ...updates, augDockStaged: stagedSlots, augShopOffers: offers, prepUnitIdBySquare: prepNext, fen: fenAfter };
              merged.uiPosition = this.computeUiPosition(this.state.game, prepNext, this.state.unitIdBySquare, augments);
              this.setState(merged);
            } else {
              // Placement failed, fall back to bench
              this.setState({ ...updates, augDockStaged: stagedSlots, augShopOffers: offers }, () => this.addBenchPiece(pieceType, unitId));
            }
          } else {
            // No square available, fall back to bench
            this.setState({ ...updates, augDockStaged: stagedSlots, augShopOffers: offers }, () => this.addBenchPiece(pieceType, unitId));
          }
        }
      }
      return true;
    } catch (e) {
      console.warn('[equip] error', e);
      return false;
    }
  }

  handleAugmentCancel(offerIndex) {
    try {
      const staged = (this.state.augDockStaged || [])[offerIndex] || null;
      if (!staged) return false;
      const pieceType = staged.pieceType;
      const unitId = staged.unitId;
      const stagedSlots = [...(this.state.augDockStaged || [null, null])];
      const isKingPiece = pieceType === 'wK';
      if (isKingPiece) {
        // Try to restore the King to the board
        const preferred = staged.lastSquare || null;
        let targetSquare = null;
        try {
          if (preferred) {
            const occ = this.state.game.get(preferred);
            if (!occ) targetSquare = preferred;
          }
        } catch (_) {}
        if (!targetSquare) {
          const files = 'abcdefgh';
          outer_k2: for (let rank = 1; rank <= 4; rank++) {
            for (let fi = 0; fi < files.length; fi++) {
              const sq = files[fi] + rank;
              try {
                const occ = this.state.game.get(sq);
                if (!occ) { targetSquare = sq; break outer_k2; }
              } catch (_) {}
            }
          }
        }
        if (!targetSquare) {
          this.showToast('No space to restore the King. Clear a square first.');
          this.triggerShakeAnimation(null, null, null);
          return false; // keep staged to avoid losing the King
        }
        const ok = this.state.game.put({ color: 'w', type: 'k' }, targetSquare);
        if (!ok) {
          this.showToast('Failed to restore the King. Try again.');
          this.triggerShakeAnimation(null, null, targetSquare);
          return false;
        }
        const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
        prepNext[targetSquare] = unitId;
        stagedSlots[offerIndex] = null;
        this.setState({
          augDockStaged: stagedSlots,
          prepUnitIdBySquare: prepNext,
          kingUnitId: unitId,
          fen: this.state.game.fen(),
          uiPosition: this.computeUiPosition(this.state.game, prepNext)
        });
      } else {
        stagedSlots[offerIndex] = null;
        // If piece came from the bench (no lastSquare), return it to the bench
        if (!staged.lastSquare) {
          this.setState({ augDockStaged: stagedSlots }, () => this.addBenchPiece(pieceType, unitId));
        } else {
          // Piece came from board - try to place it back on its original square, otherwise find an empty square, otherwise bench
          const preferred = staged.lastSquare;
          const typeLower = pieceType[1] ? pieceType[1].toLowerCase() : null;
          let targetSquare = null;
          if (typeLower) {
            try {
              if (preferred) {
                const occ = this.state.game.get(preferred);
                if (!occ) targetSquare = preferred;
              }
            } catch (_) {}
            if (!targetSquare) {
              const files = 'abcdefgh';
              outer_cancel: for (let rank = 1; rank <= 4; rank++) {
                for (let fi = 0; fi < files.length; fi++) {
                  const sq = files[fi] + rank;
                  try {
                    const occ = this.state.game.get(sq);
                    if (!occ) { targetSquare = sq; break outer_cancel; }
                  } catch (_) {}
                }
              }
            }
          }
          if (targetSquare && typeLower) {
            const placed = this.state.game.put({ color: 'w', type: typeLower }, targetSquare);
            if (placed) {
              const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
              prepNext[targetSquare] = unitId;
              this.setState({
                augDockStaged: stagedSlots,
                prepUnitIdBySquare: prepNext,
                fen: this.state.game.fen(),
                uiPosition: this.computeUiPosition(this.state.game, prepNext)
              });
            } else {
              // Placement failed, fall back to bench
              this.setState({ augDockStaged: stagedSlots }, () => this.addBenchPiece(pieceType, unitId));
            }
          } else {
            // No square available, fall back to bench
            this.setState({ augDockStaged: stagedSlots }, () => this.addBenchPiece(pieceType, unitId));
          }
        }
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  showToast(message) {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    this.setState({ toastMessage: message });
    this._toastTimer = setTimeout(() => {
      this._toastTimer = null;
      this.setState({ toastMessage: null });
    }, 4000);
  }

  dismissToast() {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    this.setState({ toastMessage: null });
  }

  triggerShakeAnimation(origin, benchId, targetSquare) {
    shakeInvalidDrop(origin, benchId, targetSquare);
  }

  renderShopSparePieces() {
    return (
      <Shop
        theme={this.props.theme}
        shop={this.state.shop}
        money={this.state.money}
        shopGlowActive={this.state.shopGlowActive}
        showShopHint={this.state.showShopHint}
        pieceRenderers={this.buildColoredPiecesRenderer(true)}
        onReroll={() => this.rerollShop()}
        onPurchase={(pt, idx) => this.purchaseFromShop(pt, idx)}
        renderMoneyPill={() => this.renderMoneyPillInverted()}
      />
    );
  }

  getPieceSellValue(pieceType) {
    if (!pieceType) return 0;
    const match = String(pieceType).match(/^[wb]([pnbrqk])/i);
    const letter = match ? match[1].toLowerCase() : (pieceType[1] ? pieceType[1].toLowerCase() : '');
    const base = this.pieceCosts[letter] || 0;
    return Math.floor(base / 2);
  }

  getAugmentColorForUnit(unitId) {
    if (!unitId) return null;
    const augmentId = this.state.augmentsByUnitId?.[unitId]?.augmentId || null;
    if (!augmentId) return null;
    return AUGMENTS[augmentId]?.color || null;
  }

  getTintedPieceType(pieceType, colorKey) {
    if (!pieceType || !colorKey) return null;
    try {
      const suffix = '_' + String(colorKey).toLowerCase();
      const candidate = pieceType + suffix;
      const piecesMap = this.buildColoredPiecesRenderer(false);
      if (piecesMap && typeof piecesMap[candidate] === 'function') {
        return candidate;
      }
    } catch (_) {}
    return null;
  }

  renderDropSlotContent(stagedPiece, options = {}) {
    const {
      size = 86,
      emptyLabel = 'drop piece here',
      tintHex = null,
      augmentColorKey = null,
      useUnitAugmentTint = false
    } = options;

    if (!stagedPiece) {
      return (
        <span style={{ fontSize: 12, color: this.props.theme === 'dark' ? '#ccc' : '#777', textAlign: 'center', padding: '8px', display: 'inline-block' }}>
          {emptyLabel}
        </span>
      );
    }

    const scale = Math.max(0.5, Math.min(1.2, size / 86));
    const uiPieceRenderers = this.buildColoredPiecesRenderer(true);
    let displayPieceType = stagedPiece.pieceType;
    let resolvedTintHex = tintHex;
    let resolvedColorKey = augmentColorKey || null;

    if (!resolvedTintHex) {
      if (!resolvedColorKey && useUnitAugmentTint && stagedPiece.unitId) {
        resolvedColorKey = this.getAugmentColorForUnit(stagedPiece.unitId);
      }
      if (resolvedColorKey) {
        const tinted = this.getTintedPieceType(stagedPiece.pieceType, resolvedColorKey);
        if (tinted) {
          displayPieceType = tinted;
        }
      }
    }

    if (resolvedTintHex) {
      const Comp = (uiPieceRenderers && uiPieceRenderers[stagedPiece.pieceType]) || defaultPieces?.[stagedPiece.pieceType] || null;
      if (Comp) {
        return (
          <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ transform: `scale(${0.9 * scale})` }}>
              <Comp fill={resolvedTintHex} />
            </div>
          </div>
        );
      }
    }

    const DisplayComp = uiPieceRenderers && uiPieceRenderers[displayPieceType];
    if (DisplayComp) {
      return (
        <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ transform: `scale(${0.9 * scale})` }}>
            <DisplayComp />
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ transform: `scale(${0.9 * scale})` }}>
          <SparePiece pieceType={displayPieceType} />
        </div>
      </div>
    );
  }

  renderSellPanel() {
    if (this.state.phase !== 'prep') return null;
    const staged = this.state.sellStagedPiece || null;
    const isExpanded = !!staged;
    const refund = staged ? (typeof staged.refundValue === 'number' ? staged.refundValue : this.getPieceSellValue(staged.pieceType)) : 0;
    const sellDisabled = !staged;
    return (
      <div
        data-sell-panel="1"
        onMouseEnter={() => this.handleSellPanelPointerEnter()}
        onTouchStart={() => this.handleSellPanelPointerEnter()}
        style={{
        border: '1px solid #555',
        borderRadius: 8,
        background: this.props.theme === 'dark' ? '#1f1f1f' : '#fafafa',
        userSelect: 'none',
        cursor: 'default',
        overflow: 'hidden',
        width: this.state.sellPanelCollapsed && !isExpanded ? 120 : 260,
        transition: 'width 260ms ease'
      }}>
        <button
          type="button"
          onClick={() => {
            if (staged) return;
            this.setState((prev) => ({ sellPanelCollapsed: !prev.sellPanelCollapsed }));
          }}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 700,
            color: this.props.theme === 'dark' ? '#eee' : '#333',
            cursor: staged ? 'default' : 'pointer'
          }}
          disabled={!!staged}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Sell Pieces</span>
            <span style={{
            transition: 'transform 220ms ease',
            transform: this.state.sellPanelCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
            fontSize: 18,
            display: 'inline-block'
            }}>▾</span>
          </span>
        </button>
        <div style={{
          maxHeight: isExpanded || !this.state.sellPanelCollapsed ? 220 : 0,
          padding: isExpanded || !this.state.sellPanelCollapsed ? '0 12px 12px' : '0 12px',
          opacity: this.state.sellPanelCollapsed && !isExpanded ? 0 : 1,
          transform: this.state.sellPanelCollapsed && !isExpanded ? 'translateY(6px)' : 'translateY(0)',
          transitionProperty: 'max-height, padding, opacity, transform',
          transitionDuration: '260ms',
          transitionTimingFunction: 'ease',
          pointerEvents: this.state.sellPanelCollapsed && !isExpanded ? 'none' : 'auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              data-sell-dropzone="1"
              style={{
                width: 86,
                height: 86,
                borderRadius: 8,
                border: `2px ${staged ? 'solid' : 'dotted'} ${staged ? (this.props.theme === 'dark' ? '#bbb' : '#666') : '#c8c8c8'}`,
                background: staged
                  ? (this.props.theme === 'dark' ? '#2f2f2f' : '#f0f0f0')
                  : (this.props.theme === 'dark' ? '#181818' : '#fff'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {this.renderDropSlotContent(staged, { size: 86, useUnitAugmentTint: true })}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: this.props.theme === 'dark' ? 'rgba(80,80,80,0.35)' : 'rgba(120,120,120,0.25)',
                pointerEvents: 'none'
              }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              <button
                onClick={() => this.handleSellConfirm()}
                disabled={sellDisabled}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #666',
                  borderRadius: 6,
                  background: sellDisabled ? (this.props.theme === 'dark' ? '#2a2a2a' : '#e0e0e0') : (this.props.theme === 'dark' ? '#2a2a2a' : '#fdfdfd'),
                  color: sellDisabled ? '#999' : (this.props.theme === 'dark' ? '#eee' : '#333'),
                  fontWeight: 800,
                  cursor: sellDisabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left'
                }}
                title={sellDisabled ? 'Drag a piece into the slot to sell it' : `Sell for $${refund}`}
              >
                {staged ? `Sell $${refund}` : 'Sell'}
              </button>
              <button
                onClick={() => this.handleSellCancel()}
                disabled={!staged}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #666',
                  borderRadius: 6,
                  background: 'transparent',
                  color: this.props.theme === 'dark' ? '#eee' : '#333',
                  fontWeight: 700,
                  cursor: staged ? 'pointer' : 'default',
                  visibility: staged ? 'visible' : 'hidden'
                }}
                title={staged ? 'Return the staged piece' : 'Drag a piece into the slot to sell it'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  handleSellConfirm() {
    const staged = this.state.sellStagedPiece || null;
    if (!staged) return;
    const refund = typeof staged.refundValue === 'number' ? staged.refundValue : this.getPieceSellValue(staged.pieceType);
    const augments = { ...(this.state.augmentsByUnitId || {}) };
    const hadAugment = staged.unitId && augments[staged.unitId];
    if (hadAugment) delete augments[staged.unitId];
    const augmentRuntime = { ...(this.state.augmentRuntime || {}) };
    const hadRuntime = staged.unitId && augmentRuntime[staged.unitId];
    if (hadRuntime) delete augmentRuntime[staged.unitId];
    const unitIdBySquare = { ...(this.state.unitIdBySquare || {}) };
    let unitMapChanged = false;
    if (staged.unitId) {
      Object.keys(unitIdBySquare).forEach((sq) => {
        if (unitIdBySquare[sq] === staged.unitId) {
          delete unitIdBySquare[sq];
          unitMapChanged = true;
        }
      });
    }
    const updates = {
      money: (this.state.money || 0) + refund,
      sellStagedPiece: null
    };
    if (hadAugment) {
      updates.augmentsByUnitId = augments;
    }
    if (hadRuntime) {
      updates.augmentRuntime = augmentRuntime;
    }
    if (unitMapChanged) {
      updates.unitIdBySquare = unitIdBySquare;
    }
    playSound('sell');
    this.setState(updates, () => {
      this.showToast(`Sold for $${refund}`);
    });
  }

  handleSellCancel() {
    this.cancelSellStaging(false);
  }

  cancelSellStaging(silent = false) {
    const staged = this.state.sellStagedPiece || null;
    if (!staged) return false;
    const finish = () => {};
    if (staged.origin === 'bench') {
      this.setState({ sellStagedPiece: null }, () => {
        this.addBenchPiece(staged.pieceType, staged.unitId);
        finish();
      });
      return true;
    }
    if (staged.origin === 'board') {
      const typeLower = staged.pieceType[1] ? staged.pieceType[1].toLowerCase() : null;
      if (!typeLower) {
        this.setState({ sellStagedPiece: null }, finish);
        return true;
      }
      // Try the original square first
      const preferred = staged.lastSquare || null;
      let targetSquare = null;
      try {
        if (preferred) {
          const occ = this.state.game.get(preferred);
          if (!occ) targetSquare = preferred;
        }
      } catch (_) {}
      // If original square is occupied, find any available square in player's area
      if (!targetSquare) {
        const files = 'abcdefgh';
        outer_sell: for (let rank = 1; rank <= 4; rank++) {
          for (let fi = 0; fi < files.length; fi++) {
            const sq = files[fi] + rank;
            try {
              const occ = this.state.game.get(sq);
              if (!occ) { targetSquare = sq; break outer_sell; }
            } catch (_) {}
          }
        }
      }
      // No available square - keep the piece staged and show error
      if (!targetSquare) {
        if (!silent) {
          this.showToast('No space to restore the piece. Clear a square first.');
          this.triggerShakeAnimation(null, null, null);
        }
        return false; // keep staged to avoid losing the piece
      }
      // Attempt to place the piece
      let restored = false;
      try {
        restored = this.state.game.put({ color: 'w', type: typeLower }, targetSquare);
      } catch (_) {
        restored = false;
      }
      if (!restored) {
        if (!silent) {
          this.showToast('Failed to restore the piece. Try again.');
          this.triggerShakeAnimation(null, null, targetSquare);
        }
        return false; // keep staged to avoid losing the piece
      }
      const prepNext = { ...(this.state.prepUnitIdBySquare || {}) };
      if (staged.unitId) {
        prepNext[targetSquare] = staged.unitId;
      }
      const uiPos = this.computeUiPosition(this.state.game, prepNext);
      this.setState({ sellStagedPiece: null, prepUnitIdBySquare: prepNext, fen: this.state.game.fen(), uiPosition: uiPos }, finish);
      return true;
    }
    this.setState({ sellStagedPiece: null }, finish);
    return true;
  }

  // --- Augment Dock UI ---
  renderAugmentDock(inLeftNav = false) {
    const rerollCost = (this._augmentStore?.kingHas && this._augmentStore.kingHas(AugmentIds.ROYAL_DECREE)) ? 1 : 2;
    return (
      <AugmentDock
        theme={this.props.theme}
        inLeftNav={inLeftNav}
        augShopOffers={this.state.augShopOffers}
        augmentsGlowActive={this.state.phase === 'prep' && !!this.state.augmentsGlowActive}
        augDockStaged={this.state.augDockStaged}
        money={this.state.money}
        rerollCost={rerollCost}
        onDismissGlow={() => {
          if (this.state.augmentsGlowActive) {
            this.setState({ augmentsGlowActive: false });
          }
        }}
        onReroll={() => this.handleAugmentReroll()}
        onBuy={(idx) => this.handleAugmentBuy(idx)}
        onCancel={(idx) => this.handleAugmentCancel(idx)}
        onSetCurrentSpare={(origin, id, idx) => this.setCurrentSpare(origin, id, idx)}
        renderDropSlotContent={(staged, options) => this.renderDropSlotContent(staged, options)}
      />
    );
  }

  getAugmentTooltip(id) {
    const name = AUGMENTS[id]?.name || '';
    const desc = describeAugment(id) || '';
    if (name && desc) return `${name}\n${desc}`;
    return name || desc || '';
  }

  updateBoardPieceTooltips() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (this._pendingTooltipFrame != null) {
      try {
        window.cancelAnimationFrame(this._pendingTooltipFrame);
      } catch (_) {}
      this._pendingTooltipFrame = null;
    }
    this._pendingTooltipFrame = window.requestAnimationFrame(() => {
      this._pendingTooltipFrame = null;
      try {
        const uiPosition = this.state?.uiPosition || {};
        const battleMap = this.state?.unitIdBySquare || {};
        const prepMap = this.state?.prepUnitIdBySquare || {};
        const augments = this.state?.augmentsByUnitId || {};
        Object.entries(uiPosition).forEach(([square, piece]) => {
          if (!square || !piece) return;
          const pieceType = piece?.pieceType || null;
          if (!pieceType) return;
          const unitId = battleMap[square] || prepMap[square] || null;
          const augmentId = unitId ? (augments?.[unitId]?.augmentId || null) : null;
          const tooltip = augmentId ? this.getAugmentTooltip(augmentId) : '';
          const elementId = `grotto-board-piece-${pieceType}-${square}`;
          const node = document.getElementById(elementId);
          if (!node) return;
          if (tooltip) {
            node.setAttribute('title', tooltip);
          } else {
            node.removeAttribute('title');
          }
        });
      } catch (_) {}
    });
  }

  renderBenchSparePieces() {
    return (
      <Bench
        theme={this.props.theme}
        benchIds={this.state.benchIds}
        benchNonce={this.state.benchNonce}
        benchGlowActive={this.state.benchGlowActive}
        showBenchHint={this.state.showBenchHint}
        unitIdByBenchId={this.state.unitIdByBenchId}
        augmentsByUnitId={this.state.augmentsByUnitId}
        ensureBenchPieceVariant={(pt, colorKey, id) => this.ensureBenchPieceVariant(pt, colorKey, id)}
        getAugmentTooltip={(id) => this.getAugmentTooltip(id)}
        getUiPieceRenderer={(key) => this.getUiPieceRenderer(key)}
        onSetCurrentSpare={(origin, pt, id) => this.setCurrentSpare(origin, pt, id)}
      />
    );
  }

  // --- Missing helpers and game flow methods ---

  getWhiteKingOnlyFen() {
    return WHITE_KING_ONLY_FEN;
  }

  // removed buttons for kingsOnly/clearBoard; keep helpers for potential future use

  newGame() {
    const empty = new Chess();
    const startingFen = WHITE_KING_ONLY_FEN;
    empty.load(startingFen);
    const uiPos = this.computeUiPosition(empty, {}, {});
    const newAugSeed = generateAugmentSeed();
    
    const base = createBaseState(newAugSeed, startingFen);
    base.game = empty;
    base.uiPosition = uiPos;
    base.shop = generateShop();
    this.setState(base, () => {
      try { this.engine.dispose(); } catch (e) {}
      this.engine = new StockfishAdapter('stockfish.asm.js');
      this.engine.init();
      this.engine.onBestMove((mv) => this.handleEngineBestMove(mv));
      this.engine.onInfo((info) => this.handleEngineInfo(info));
      this.engine.onError((e) => {
        console.warn('[engine] error, attempting fallback', e);
        this.showToast('Engine error. Trying fallback...');
      });
      this._engineSearchTurn = 'w';
      // Repopulate augment shop offers on restart
      try {
        this.setState({ augShopOffers: this.getAugmentShopOffers(2, this.state.money, 0), augRollNonce: 0 });
      } catch (_) {}
    });
  }

  randomizeOpponentPreview() {
    if (this.state.phase !== 'prep') return;
    try {
      // Keep current white setup; regenerate a black preview using same logic as round start
      const whitePieces = extractWhitePiecesFromFen(this.state.game);
      const blackBudget = getOpponentBudget(this.state.roundNumber || 1);
      let previewFen = null;
      let attempts = 0;
      while (!previewFen && attempts < 50) {
        const blackPieces = generateBlackSetup(this.state.roundNumber, blackBudget);
        previewFen = buildBattleFen(whitePieces, blackPieces);
        attempts++;
      }
      if (!previewFen) {
        this.showToast('Failed to generate preview. Try again.');
        return;
      }
      const game = new Chess();
      game.load(previewFen);
      this.setState({ fen: previewFen, game, evalInfo: null, uiPosition: this.computeUiPosition(game) }, () => {
        this.updateStockfishPosition();
        this.engine.goMovetime(250);
      });
    } catch (e) {
      console.warn('[randomizeOpponentPreview] error', e);
    }
  }

  resetAutoPreviewTimer() {
    if (this._autoPreviewTimer) {
      clearTimeout(this._autoPreviewTimer);
      this._autoPreviewTimer = null;
    }
    if (this.state?.autoPreviewEnabled && this.state?.phase === 'prep') {
      this._autoPreviewTimer = setTimeout(() => {
        this._autoPreviewTimer = null;
        this.randomizeOpponentPreview();
        // schedule next cycle
        this.resetAutoPreviewTimer();
      }, 5000);
    }
  }

  confirmNewGame() {
    this.newGame();
  }

  loadFenFromInput() {
    const input = document.getElementById(this.fenStringInputId);
    if (!input) return;
    const fen = (input.value || '').trim();
    if (!fen) return;
    const test = new Chess();
    let ok = false;
    try {
      ok = test.load(fen);
    } catch (e) {
      ok = false;
    }
    if (!ok) {
      console.warn('[loadFenFromInput] invalid FEN');
      return;
    }
    this.setState({ fen, game: test, uiPosition: this.computeUiPosition(test) });
  }

  addBenchPiece(pieceType, unitId = null) {
    if (!pieceType) return;
    const m = String(pieceType).match(/^([wb])([pnbrqk])/i);
    const base = m ? (m[1].toLowerCase() + m[2].toUpperCase()) : pieceType;
    this.setState((prevState) => {
      const bench = { ...(prevState?.bench || {}) };
      const benchIds = { ...(prevState?.benchIds || {}) };
      const unitIdByBenchId = { ...(prevState?.unitIdByBenchId || {}) };
      const currentCount = bench[base] || 0;
      bench[base] = currentCount + 1;
      const id = (prevState?.nextBenchId != null ? prevState.nextBenchId : 1);
      benchIds[base] = [...(benchIds[base] || []), id];
      let uid = unitId;
      let nextUnitId = prevState?.nextUnitId || 1;
      if (!uid) {
        uid = 'u' + nextUnitId;
        nextUnitId += 1;
      }
      unitIdByBenchId[id] = uid;
      const benchGlowActive = prevState?.benchGlowAllowed ? true : prevState?.benchGlowActive;
      return {
        bench,
        benchIds,
        nextBenchId: id + 1,
        benchNonce: (prevState?.benchNonce || 0) + 1,
        benchGlowActive,
        unitIdByBenchId,
        nextUnitId
      };
    });
  }

  removeBenchPiece(pieceType, benchId = null) {
    if (!pieceType) return;
    const m = String(pieceType).match(/^([wb])([pnbrqk])/i);
    const base = m ? (m[1].toLowerCase() + m[2].toUpperCase()) : pieceType;
    this.setState((prevState) => {
      const currentCount = (prevState?.bench?.[base] || 0);
      if (currentCount <= 0) return null;
      const bench = { ...(prevState?.bench || {}) };
      bench[base] = Math.max(0, currentCount - 1);
      const benchIds = { ...(prevState?.benchIds || {}) };
      let ids = [...(benchIds[base] || [])];
      if (benchId != null) {
        ids = ids.filter(id => id !== benchId);
      } else {
        ids.pop();
      }
      benchIds[base] = ids;
      const unitIdByBenchId = { ...(prevState?.unitIdByBenchId || {}) };
      if (benchId != null) delete unitIdByBenchId[benchId];
      const totalRemaining = Object.values(bench).reduce((sum, v) => sum + (v || 0), 0);
      const benchGlowActive = prevState?.benchGlowAllowed ? totalRemaining > 0 : false;
      return {
        bench,
        benchIds,
        benchNonce: (prevState?.benchNonce || 0) + 1,
        benchGlowActive,
        unitIdByBenchId
      };
    });
  }

  scheduleBenchUpdate(work) {
    // Defer bench mutations until the next frame so dnd-kit can finish
    // dispatching the current drag end event before we unmount the draggable.
    if (typeof work !== 'function') return;
    const runner = () => {
      try { work(); } catch (err) { console.error('[scheduleBenchUpdate] failed', err); }
    };
    try {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => runner());
      } else {
        setTimeout(() => runner(), 0);
      }
    } catch (_) {
      runner();
    }
  }

  startBattle() {
    if (this.state.phase !== 'prep') return;
    // Prevent starting if the king is staged in an augment buy slot
    const stagedSlots = this.state.augDockStaged || [];
    const kingStaged = stagedSlots.some(slot => slot && slot.pieceType === 'wK');
    if (kingStaged) {
      this.showToast('Cannot start battle: King is staged for an augment purchase.');
      this.triggerShakeAnimation(null, null, null);
      return;
    }
    let whiteSnapshotFen = null;
    let whiteSourceGame = this.state.game;
    if (this.state.showEnemyPreview) {
      try {
        const whitePieces = extractWhitePiecesFromFen(this.state.game);
        const snapshotFen = fenFromPlacements({ whitePieces, blackPieces: [], sideToMove: 'w', castling: '-' });
        if (snapshotFen) {
          const snapshotGame = new Chess();
          if (snapshotGame.load(snapshotFen)) {
            whiteSourceGame = snapshotGame;
            whiteSnapshotFen = snapshotFen;
          }
        }
      } catch (err) {
        console.warn('[startBattle] failed to derive preview snapshot', err);
      }
      if (!whiteSnapshotFen && this.state.prepSetupFen) {
        try {
          const fallback = new Chess();
          if (fallback.load(this.state.prepSetupFen)) {
            whiteSourceGame = fallback;
            whiteSnapshotFen = this.state.prepSetupFen;
          }
        } catch (_) {}
      }
    }
    if (!whiteSnapshotFen) {
      whiteSnapshotFen = this.state.game.fen();
    }
    this.startingFenBeforeBattle = whiteSnapshotFen;
    // Capture starting white material value for display during the round
    let startingWhiteValue = 0;
    try {
      // Recompute material from the white setup snapshot to avoid black preview pieces inflating the value
      const snapshotGame = new Chess();
      snapshotGame.load(whiteSnapshotFen);
      const board = snapshotGame.board?.() || [];
      const values = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
      for (let r = 0; r < board.length; r++) {
        const row = board[r] || [];
        for (let c = 0; c < row.length; c++) {
          const piece = row[c];
          if (piece && piece.color === 'w') {
            startingWhiteValue += values[piece.type] || 0;
          }
        }
      }
    } catch (_) {
      try { startingWhiteValue = (this.getMaterial()?.white || 0); } catch (_) { startingWhiteValue = 0; }
    }
    // Extract current white placements
    const whitePieces = extractWhitePiecesFromFen(whiteSourceGame);
    // Generate black with cumulative budget from $2/round (+$1 per-round income every 5 rounds)
    const blackBudget = getOpponentBudget(this.state.roundNumber || 1);
    let battleFen = null;
    let attempts = 0;
    while (!battleFen && attempts < 50) {
      const blackPieces = generateBlackSetup(this.state.roundNumber, blackBudget);
      battleFen = buildBattleFen(whitePieces, blackPieces);
      attempts++;
    }
    if (!battleFen) {
      this.showToast('Failed to generate a legal Black setup. Try again.');
      return;
    }
    const game = new Chess();
    game.load(battleFen);
    // Build battle unitIdBySquare mapping from prep mapping for white pieces
    const unitIdBySquare = {};
    const prepMap = this.state.prepUnitIdBySquare || {};
    try {
      const board = game.board();
      const files = 'abcdefgh';
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          const p = board[r][c];
          if (p && p.color === 'w') {
            const sq = files[c] + (8 - r);
            const uid = prepMap[sq] || null;
            if (uid) unitIdBySquare[sq] = uid;
          }
        }
      }
    } catch (_) {}
    this._augmentGoldPending = 0;
    this.setState({ phase: 'battle', started: Date.now(), fen: battleFen, game, whiteSetupSnapshotFen: whiteSnapshotFen, startGlowAllowed: false, showStartGlow: false, evalInfo: null, startingWhiteValue, unitIdBySquare, augmentRuntime: {}, augmentGoldBattle: 0, uiPosition: this.computeUiPosition(game, this.state.prepUnitIdBySquare, unitIdBySquare), showEnemyPreview: false, prepSetupFen: null, enemyPreviewGlowActive: false, augmentsGlowActive: false }, () => {
      // Round start: static effects like Crown Tax
      try { this.augmentApi.onRoundStart(); } catch (_) {}
      this.updateStockfishPosition();
      this.engineNextStep();
    });
  }

  updateStockfishPosition() {
    if (!this.engine) return;
    // Capture side-to-move for this position before starting search
    try { this._engineSearchTurn = this.state.game?.turn?.() || 'w'; } catch (_) { this._engineSearchTurn = 'w'; }
    try { this.engine.stop(); } catch (_) {}
    this.engine.setPosition(this.state.game.fen());
    // Reset illegal-move accumulation on each fresh position sync
    this._consecutiveIllegalEngineMoves = 0;
    // Reset smoothing on new positions to avoid carryover jitter
    this._evalSmoothedPercent = 0.5;
    this._evalLastUpdateMs = Date.now();
  }

  engineNextStep() {
    const fullMoves = getFullMoveCount(this.state.game);
    if (shouldForceDraw(fullMoves)) {
      this.handleRoundEnd('draw');
      return;
    }
    // Cache side to move for this search to normalize evals consistently
    try { this._engineSearchTurn = this.state.game?.turn?.() || 'w'; } catch (_) { this._engineSearchTurn = 'w'; }
    this.updateStockfishPosition();
    const policy = selectEnginePolicy(fullMoves, this.state.game);
    if (policy.type === 'depth') {
      this.engine.goDepth(policy.value);
    } else if (policy.type === 'movetime') {
      this.engine.goMovetime(policy.value);
    } else if (policy.type === 'forceDraw') {
      this.handleRoundEnd('draw');
    }
  }

  getNextIncome(currentMoney) {
    return calculateIncome(currentMoney);
  }

  handleRoundEnd(result = null) {
    let baseGain = 0;
    if (result === 'win') baseGain = 3;
    else if (result === 'draw') baseGain = 1;
    else baseGain = 0;
    const income = this.getNextIncome(this.state.money);
    const rewardDelta = this._augmentGoldPending || 0;
    const newMoney = this.state.money + baseGain + income + rewardDelta;
    // Precompute next round augment offers to avoid interim render showing consumed slots
    let nextAugOffers = [];
    try {
      nextAugOffers = this.getAugmentShopOffers(2, newMoney, 0);
    } catch (_) {
      nextAugOffers = [];
    }
    // Ensure engine is stopped before resetting state
    try { this.engine?.stop?.(); } catch (_) {}
    // Restore to player's snapshot setup
    const fen = this.state.whiteSetupSnapshotFen || this.getWhiteKingOnlyFen();
    const game = new Chess();
    game.load(fen);
    const uiPos = this.computeUiPosition(game, this.state.prepUnitIdBySquare, {});
    const nextRound = (this.state.roundNumber || 1) + 1;
    this._augmentGoldPending = 0;
    this.setState({
      phase: 'prep',
      started: null,
      fen,
      game,
      uiPosition: uiPos,
      money: newMoney,
      roundNumber: nextRound,
      shop: generateShop(),
      // Reset augment UI state on new setup round
      augDockStaged: [null, null],
      augShopOffers: nextAugOffers,
      augRollNonce: 0,
      augmentGoldBattle: 0,
      augmentRuntime: {},
      unitIdBySquare: {},
      // Disable bench glow after first round; it should never reappear
      benchGlowAllowed: nextRound <= 1,
      benchGlowActive: false,
      enemyPreviewGlowActive: nextRound === 2,
      augmentsGlowActive: !this.props.allowAugRound1 && nextRound === 3,
      evalInfo: null,
      showEnemyPreview: false,
      prepSetupFen: null
    }, () => {
      // Apply any bench rewards queued by augments at round start
      try { this.applyPendingBenchRewards(); } catch (_) {}
      // Ensure eval number is computed immediately on returning to setup
      try {
        this.updateStockfishPosition();
        this.engine.goMovetime(200);
      } catch (_) {}
    });
  }

  tick() {
    // Minimal tick loop; can be expanded for timers/rounds
    window.setTimeout(() => this.tick(), this.tickRate);
  }

  getMaterial() {
    return computeMaterial(this.state.game);
  }

  checkSidePieces(color) {
    return hasSidePieces(this.state.game, color);
  }

  renderEvalBar() {
    return (
      <EvalBar
        evalInfo={this.state.evalInfo}
        material={this.getMaterial()}
        theme={this.props.theme}
      />
    );
  }

  renderOpponentPreviewOverlay() {
    const height = 560;
    const half = height / 2;
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: half, background: 'rgba(0,0,0,0.35)' }} />
        <div style={{ position: 'absolute', top: half - 1, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.35)' }} />
      </div>
    );
  }

  // Visual augment tint overlay for on-board white pieces during prep/battle
  renderBoardAugmentOverlays() {
    return null;
  }

  renderOpponentPreviewCard() {
    const mat = this.getMaterial();
    return (
      <OpponentPreviewCard
        theme={this.props.theme}
        roundNumber={this.state.roundNumber}
        phase={this.state.phase}
        game={this.state.game}
        blackBudget={getOpponentBudget(this.state.roundNumber || 1)}
        whiteValue={mat?.white || 0}
        startingWhiteValue={this.state.startingWhiteValue}
        showStartGlow={this.state.showStartGlow}
        startGlowAllowed={this.state.startGlowAllowed}
        showEnemyPreview={this.state.showEnemyPreview}
        enemyPreviewGlowActive={this.state.enemyPreviewGlowActive}
        showResultOverlay={this.state.showResultOverlay}
        oppBtnHover={this.state.oppBtnHover}
        oppBtnActive={this.state.oppBtnActive}
        onStartBattle={() => this.startBattle()}
        onPresentRoundResult={(result) => this.presentRoundResult(result)}
        onShowPreview={() => this.handleShowPreview()}
        onClearPreview={() => this.handleClearPreview()}
        onSetOppBtnHover={(v) => this.setState({ oppBtnHover: v })}
        onSetOppBtnActive={(v) => this.setState({ oppBtnActive: v })}
      />
    );
  }

  handleShowPreview() {
    if (!this.state.showEnemyPreview) {
      try { this.engine?.stop?.(); } catch (_) {}
      const currentFen = this.state.game?.fen?.() || this.state.fen;
      this.setState({ prepSetupFen: currentFen, showEnemyPreview: true, enemyPreviewGlowActive: false }, () => {
        this.randomizeOpponentPreview();
      });
    } else {
      this.setState({ enemyPreviewGlowActive: false }, () => {
        this.randomizeOpponentPreview();
      });
    }
  }

  handleClearPreview() {
    try { this.engine?.stop?.(); } catch (_) {}
    let restoreFen = null;
    try {
      const whitePieces = extractWhitePiecesFromFen(this.state.game) || [];
      if (whitePieces.length > 0) {
        const candidateFen = fenFromPlacements({ whitePieces, blackPieces: [], sideToMove: 'w', castling: '-' });
        if (candidateFen) {
          const test = new Chess();
          if (test.load(candidateFen)) {
            restoreFen = candidateFen;
          }
        }
      }
    } catch (err) {
      console.warn('[clear enemy preview] failed to build snapshot', err);
    }
    if (!restoreFen) {
      restoreFen = this.state.prepSetupFen || this.getWhiteKingOnlyFen();
    }
    const game = new Chess();
    game.load(restoreFen);
    const uiPos = this.computeUiPosition(game, this.state.prepUnitIdBySquare, {});
    this.setState({
      showEnemyPreview: false,
      fen: restoreFen,
      game,
      uiPosition: uiPos,
      evalInfo: null,
      prepSetupFen: restoreFen
    });
  }

  renderMaterialPanel() {
    const mat = this.getMaterial();
    const tile = (label, value, bg) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: bg, border: '1px solid #555', borderRadius: 6, minWidth: 120 }}>
        <span style={{ fontWeight: 700 }}>{label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tile('White', mat.white, this.props.theme === 'dark' ? '#2a2a2a' : '#f7f7f7')}
        {tile('Black', mat.black, this.props.theme === 'dark' ? '#2a2a2a' : '#f7f7f7')}
      </div>
    );
  }
}

export default Game;